<template>
  <div>
    <!-- Hero Profile Header -->
    <div class="profile-hero-card">
      <div class="hero-main-info">
        <div class="hero-avatar">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
        </div>
        <div class="hero-titles">
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <h1 class="hero-name">
              {{ (childInfo && childInfo.childxfamily_name) ? (childInfo.childxfamily_name + ' ' + (childInfo.childxsure_name || '')) : (isDancerRoute ? 'Saját adataim' : 'Diák és szülő információk') }}
            </h1>
            <span 
              v-if="childInfo && childInfo.status && childInfo.status.String"
              class="status-badge"
              :class="childInfo.status.String === 'Active' ? 'status-active' : 'status-inactive'"
            >
              {{ childInfo.status.String === 'Active' ? 'Aktív táncos' : 'Inaktív' }}
            </span>
          </div>
          <p class="hero-subtitle" v-if="!isDancerRoute">Táncos profiljának és naptárának kezelése</p>
          <p class="hero-subtitle" v-else>Saját táncos profilod és naptárad kezelése</p>
        </div>
      </div>

      <!-- Action Button -->
      <div class="hero-actions d-flex align-items-center gap-2 flex-wrap" v-if="isAdmin || canEdit">
        <!-- Direct Billing Link -->
        <router-link
          :to="'/children/' + childId + '/billing'"
          class="modern-btn modern-btn--primary"
          title="Számlázási és befizetési modul megnyitása"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="2" y="5" width="20" height="14" rx="2"/>
            <line x1="2" y1="10" x2="22" y2="10"/>
          </svg>
          Számlázás / Egyenleg
        </router-link>

        <button 
          v-if="!isEditingEnabled" 
          class="modern-btn modern-btn--secondary" 
          @click="$emit('enable-editing')"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
          Adatok módosítása
        </button>
        <button 
          v-if="isEditingEnabled" 
          class="modern-btn modern-btn--secondary" 
          @click="$emit('disable-editing')"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
          </svg>
          Módosítás letiltása
        </button>

        <!-- Admin Direct Delete Button -->
        <button
          v-if="isAdmin"
          class="modern-btn modern-btn--danger"
          @click="$emit('open-delete-dancer')"
          title="Táncos végleges törlése az adatbázisból"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            <line x1="10" y1="11" x2="10" y2="17"/>
            <line x1="14" y1="11" x2="14" y2="17"/>
          </svg>
          Táncos törlése
        </button>

        <!-- Dancer / Parent Request Deletion Button -->
        <button
          v-if="!isAdmin"
          class="modern-btn modern-btn--danger"
          style="background-color: #ef4444; border-color: #ef4444;"
          @click="$emit('open-delete-request')"
          title="Táncos törlésének kérelmezése az admintól"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            <line x1="10" y1="11" x2="10" y2="17"/>
            <line x1="14" y1="11" x2="14" y2="17"/>
          </svg>
          Törlés kérése
        </button>
      </div>
    </div>

    <div v-if="childInfo" class="mt-4">
      <AddNewChldItem
        v-if="!isAdmin && !isTeacher"
        :parent_1_mail="loggedInParentEmail || (childInfo ? childInfo.parent1Email : '') || ''"
        :parent_2_mail="parentEmail || parent2Email"
        :default_familyname="(childInfo ? (childInfo.childxfamily_name || childInfo.parent1xfamily_name) : '') || ''"
        :default_addr="(childInfo ? (childInfo.childxaddr || childInfo.parent1xaddr) : '') || ''"
        @child-added="$emit('child-added')"
      />

      <!-- Sibling Switcher Bar for Parents with multiple children -->
      <div v-if="!isDancerRoute && siblingsList && siblingsList.length > 1" class="sibling-switcher-card mt-4">
        <div class="switcher-info">
          <div class="switcher-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <span class="switcher-label">Gyermekeim (Profilváltás):</span>
        </div>
        <div class="sibling-pills">
          <router-link
            v-for="s in siblingsList"
            :key="s.id"
            :to="'/children/' + s.id"
            class="sibling-pill"
            :class="{ 'sibling-pill--active': s.id == childId }"
          >
            👤 {{ s.fullName }}
          </router-link>
          <router-link
            v-if="isParent"
            to="/children"
            class="sibling-pill sibling-pill--add"
            style="background: rgba(99, 102, 241, 0.1); border-color: rgba(99, 102, 241, 0.3); color: #4f46e5; font-weight: 600;"
            title="Összes gyermek megtekintése vagy új hozzáadása"
          >
            ➕ Új gyermek / Lista
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AddNewChldItem from '../AddNewChldItem.vue';

export default {
  name: 'ChildHeroHeader',
  components: {
    AddNewChldItem
  },
  props: {
    childInfo: {
      type: Object,
      default: null
    },
    childId: {
      type: [String, Number],
      default: ''
    },
    isDancerRoute: {
      type: Boolean,
      default: false
    },
    isAdmin: {
      type: Boolean,
      default: false
    },
    isTeacher: {
      type: Boolean,
      default: false
    },
    isParent: {
      type: Boolean,
      default: false
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    isEditingEnabled: {
      type: Boolean,
      default: false
    },
    siblingsList: {
      type: Array,
      default: () => []
    },
    loggedInParentEmail: {
      type: String,
      default: ''
    },
    parentEmail: {
      type: String,
      default: ''
    },
    parent2Email: {
      type: String,
      default: ''
    }
  },
  emits: [
    'enable-editing',
    'disable-editing',
    'open-delete-dancer',
    'open-delete-request',
    'child-added'
  ]
};
</script>

<style scoped>
.profile-hero-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.75rem 2rem;
  background: var(--card-bg);
  border-radius: 20px;
  border: 1px solid var(--color-border);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  flex-wrap: wrap;
  gap: 1.25rem;
}

.hero-main-info {
  display: flex;
  align-items: center;
  gap: 1.25rem;
}

.hero-avatar {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, var(--primary-blue, #2563eb), var(--secondary-blue, #0ea5e9));
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25);
}

.hero-name {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--color-heading);
  margin: 0;
  line-height: 1.2;
}

.hero-subtitle {
  color: var(--color-text-muted);
  font-size: 0.95rem;
  margin: 0.25rem 0 0 0;
}

.status-badge {
  font-size: 0.775rem;
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
}

.status-active {
  background-color: rgba(34, 197, 94, 0.15);
  color: #16a34a;
  border: 1px solid rgba(34, 197, 94, 0.3);
}

.status-inactive {
  background-color: rgba(148, 163, 184, 0.15);
  color: #64748b;
  border: 1px solid rgba(148, 163, 184, 0.3);
}

.sibling-switcher-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  background: var(--card-bg);
  border-radius: 16px;
  border: 1px solid var(--color-border);
  flex-wrap: wrap;
  gap: 1rem;
}

.switcher-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.switcher-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: rgba(37, 99, 235, 0.1);
  color: var(--primary-blue, #2563eb);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.switcher-label {
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--color-heading);
}

.sibling-pills {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.sibling-pill {
  padding: 0.4rem 1rem;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.85rem;
  color: var(--color-text);
  background: var(--color-background-soft);
  border: 1px solid var(--color-border);
  text-decoration: none;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.sibling-pill:hover {
  background: var(--hover-bg, rgba(37, 99, 235, 0.08));
  border-color: var(--primary-blue, #2563eb);
  color: var(--primary-blue, #2563eb);
}

.sibling-pill--active {
  background: var(--primary-blue, #2563eb) !important;
  color: #ffffff !important;
  border-color: var(--primary-blue, #2563eb) !important;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.35);
}

[data-theme="dark"] .sibling-switcher-card {
  background: var(--gray-800) !important;
  border-color: var(--gray-700) !important;
}

[data-theme="dark"] .switcher-icon {
  background: rgba(37, 99, 235, 0.2) !important;
  color: #60a5fa !important;
}

[data-theme="dark"] .sibling-pill {
  background: var(--gray-900) !important;
  border-color: var(--gray-700) !important;
  color: var(--gray-200) !important;
}
</style>
